import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {
  StyledBudgetActiveRightSideContainer,
  StyledBudgetActivePageContainer,
  StyledBudgetActivePageContent,
  StyledBudgetActivePageTabs,
} from './StyledBudgetActiveDetailsPage';
import NotFoundPage from '../NotFoundPage';
import PageContainer from '../../components/PageContainer';
import SpinSmall from '../../components/SpinSmall';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import AnimatedSwitcher from '../../components/animations/AnimatedSwitcher';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import ExpenseAgentChat from '../../components/pages/OverviewPage/AgentChatPanel';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader';
import BudgetActiveTabs from '../../components/pages/BudgetRequestDetailsPage/BudgetActiveTabs';
import BudgetActiveInfo from '../../components/pages/BudgetActiveDetailsPage/BudgetActiveInfo';
import BudgetRequestInfo from '../../components/pages/BudgetRequestDetailsPage/BudgetRequestInfo';
import BudgetRequestTabs from '../../components/pages/BudgetRequestDetailsPage/BudgetRequestTabs';
import routes from '../../routes/routes.json';
import {budgetActions} from '../../state/actions/budget.actions';
import {budgetHelpers} from '../../utils/helpers';

const getBudgetName = budgetHelpers.getBudgetName;

const BudgetActiveDetailsPage = ({
  ...props
}) => {
  const uriParams = useParams();
  const [t] = useTranslation(['main', 'budgets']);
  const [pageLoading, setPageLoading] = useState({loading: true, exists: true});
  const dispatch = useDispatch();
  const budget = useSelector(state => state?.budget?.budgetRequest);

  const [activeTab, setActiveTab] = React.useState('budget');

  const title = `${t('pageTitles.budgetsActive')} | ${getBudgetName(budget)}`;

  const breadcrumbs = [{
    title: t('pageTitles.budgetsActive'), href: routes.budgetsActive }, { title: getBudgetName(budget) }
  ];

  useEffect(() => {
    return () => dispatch(budgetActions.clearBudgetRequest());
  }, [dispatch]);

  const budgetId = useMemo(() => uriParams?.budgetId, [uriParams]);

  useEffect(() => {
    if (!budgetId) return;

    dispatch(
      budgetActions.getBudgetRequest({
        id: budgetId,
        successCallback: () => setPageLoading({loading: false, exists: true}),
        errorCallback: () => setPageLoading({loading: false, exists: false})
      })
    );
  }, [budgetId, dispatch]);

  const tabs = [
    {
      key: 'budget',
      label: t('budgets:budgetActiveTabs.budget'),
      children: <BudgetActiveInfo />
    },
    {
      key: 'request-details',
      label: t('budgets:budgetActiveTabs.requestDetails'),
      children: (
        <StyledBudgetActivePageContent size='large' direction='vertical'>
          <BudgetRequestInfo onlyReadView={true} />
        </StyledBudgetActivePageContent>
      )
    },
  ];

  const tabsRightItems = [
    {
      key: 'budget',
      component: <BudgetActiveTabs />
    },
    {
      key: 'request-details',
      component: <BudgetRequestTabs />
    },
  ];

  const {exists, loading} = pageLoading;

  return (
    <PageContainer {...props}>
      <PageDocumentDetails title={title} />
      <BalancePageHeader breadcrumbs={breadcrumbs} />
      <DashboardPageTransition>
        <SpinSmall spinning={loading}>
          {exists ? (
            <StyledBudgetActivePageContainer>
              <StyledBudgetActivePageTabs activeKey={activeTab} onChange={setActiveTab} items={tabs} type='card' />
              <StyledBudgetActiveRightSideContainer>
                <AnimatedSwitcher items={tabsRightItems} displayedKey={activeTab} />
              </StyledBudgetActiveRightSideContainer>
            </StyledBudgetActivePageContainer>
          ) : !loading && <NotFoundPage />}
        </SpinSmall>
      </DashboardPageTransition>
      <ExpenseAgentChat />
    </PageContainer>
  );
}

export default BudgetActiveDetailsPage;
